import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PendingStudios from './PendingStudios';
import StudioList from './StudioList';
import AffiliateSettings from './AffiliateSettings';

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

const SuperAdminDashboard = () => {
    const [studios, setStudios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [message, setMessage] = useState(null);
    const [stats, setStats] = useState({ totalClientCount: 0, pendingCount: 0, approvedCount: 0 });
    const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'pending', 'studios'

    useEffect(() => {
        fetchStudios();
    }, []);

    const fetchStudios = async () => {
        try {
            const response = await axios.get(`${API_URL}/studios`); // Super Admin endpoint (lists all)
            const data = Array.isArray(response.data) ? response.data : (response.data?.data ? response.data.data : []);
            const validData = Array.isArray(data) ? data : [];
            setStudios(validData);

            // Calculate basic stats
            const pending = validData.filter(s => s.status === 'Pending').length;
            const approved = validData.filter(s => s.status === 'Approved').length;
            setStats(prev => ({ ...prev, pendingCount: pending, approvedCount: approved }));

        } catch (error) {
            console.error("Error fetching studios", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (searchTerm.length < 3) return;

        try {
            const res = await axios.get(`${API_URL}/admin/search?q=${searchTerm}`);
            setSearchResults(res.data);
        } catch (error) {
            console.error("Search failed", error);
        }
    };

    const updateStudioStatus = async (id, newStatus) => {
        if (!window.confirm(`Are you sure you want to set this studio to ${newStatus}?`)) return;

        try {
            const res = await axios.put(`${API_URL}/admin/studios/${id}/status`, { status: newStatus });
            if (res.data.success) {
                alert(`Success: Studio ${newStatus} successfully.`);
                setMessage(`Studio ${newStatus} successfully.`);
                fetchStudios(); // Refresh list
            } else {
                alert(`Error: ${res.data.message || 'Failed to update'}`);
            }
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            console.error("Status update failed", error);
            const msg = error.response?.data?.message || "Failed to update status.";
            alert(`Error: ${msg}`);
            setMessage(msg);
        }
    };

    const updateStudio = async (id, updatedData) => {
        try {
            const res = await axios.put(`${API_URL}/studios/${id}`, updatedData);
            if (res.data.success) {
                setMessage("Studio updated successfully.");
                fetchStudios(); // Refresh list to get updated details
                return true;
            } else {
                alert(`Error: ${res.data.message || 'Failed to update'}`);
                return false;
            }
        } catch (error) {
            console.error("Studio update failed", error);
            const msg = error.response?.data?.message || "Failed to update studio.";
            alert(`Error: ${msg}`);
            return false;
        }
    };

    if (loading) return <div className="p-8 text-white">Loading Admin Dashboard...</div>;

    return (
        <div className="min-h-screen bg-gray-900 text-white p-6 md:p-12">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-10 gap-6">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600 uppercase tracking-tight">
                            Super Admin "God Mode"
                        </h1>
                        <p className="text-gray-400 mt-2 text-sm">Manage Tenants & Global Search</p>
                    </div>
                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:flex gap-4 w-full sm:w-auto">
                        <div className="bg-gray-800 p-4 rounded-xl text-center min-w-[120px] border border-gray-700 shadow-lg flex-1">
                            <span className="block text-2xl font-black text-green-400">{stats.approvedCount}</span>
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Active Studios</span>
                        </div>
                        <div className="bg-gray-800 p-4 rounded-xl text-center min-w-[120px] border border-gray-700 shadow-lg flex-1">
                            <span className="block text-2xl font-black text-yellow-400">{stats.pendingCount}</span>
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Pending</span>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 sm:gap-4 border-b border-gray-700 mb-8 overflow-x-auto no-scrollbar whitespace-nowrap">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'overview' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'pending' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        Pending Approvals ({stats.pendingCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('studios')}
                        className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'studios' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        All Studios
                    </button>
                    <button
                        onClick={() => setActiveTab('affiliate')}
                        className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider transition-colors ${activeTab === 'affiliate' ? 'text-purple-400 border-b-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}
                    >
                        Affiliate Settings
                    </button>
                </div>

                {message && (
                    <div className="bg-blue-600/20 border border-blue-500 text-blue-200 px-4 py-3 rounded mb-6">
                        {message}
                    </div>
                )}

                {/* TABS CONTENT */}

                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && (
                    <>
                        {/* GOD MODE SEARCH */}
                        <div className="bg-gray-800 rounded-xl p-6 mb-10 border border-gray-700 shadow-xl">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <span className="text-2xl">👁️</span> Global Client Search
                            </h2>
                            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-4">
                                <input
                                    type="text"
                                    placeholder="Search client name or email..."
                                    className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500 transition-colors text-white text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <button type="submit" className="bg-purple-600 hover:bg-purple-700 px-8 py-3 rounded-lg font-bold transition-colors uppercase text-xs tracking-widest shadow-lg shadow-purple-900/20">
                                    Search
                                </button>
                            </form>

                            {searchResults.length > 0 && (
                                <div className="overflow-x-auto no-scrollbar custom-scrollbar">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="text-gray-400 border-b border-gray-700">
                                                <th className="p-3">Client Name</th>
                                                <th className="p-3">Email</th>
                                                <th className="p-3">Studio</th>
                                                <th className="p-3">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(searchResults) && searchResults.map(client => (
                                                <tr key={client.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                                                    <td className="p-3 font-medium">{client.first_name} {client.last_name}</td>
                                                    <td className="p-3 text-gray-400">{client.email}</td>
                                                    <td className="p-3 text-purple-400">{client.studio_name}</td>
                                                    <td className="p-3">
                                                        <span className="text-xs bg-gray-700 px-2 py-1 rounded">View Only</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* QUICK ACTION STUDIOS (ACTIVE) */}
                        <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-xl overflow-hidden">
                            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
                                <h2 className="text-xl font-bold">Studio Overview</h2>
                                <button onClick={() => setActiveTab('studios')} className="text-sm text-purple-400 hover:text-purple-300 font-bold uppercase">View All</button>
                            </div>
                            <div className="overflow-x-auto no-scrollbar custom-scrollbar">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-700/50 text-gray-300 uppercase text-xs tracking-wider">
                                        <tr>
                                            <th className="p-4">Studio Name</th>
                                            <th className="p-4">Owner</th>
                                            <th className="p-4">Status</th>
                                            <th className="p-4">Lifecycle Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700">
                                        {Array.isArray(studios) && studios.slice(0, 5).map(studio => {
                                            const safeStatus = (studio.status || 'Active').charAt(0).toUpperCase() + (studio.status || 'Active').slice(1).toLowerCase();
                                            return (
                                                <tr key={studio.id} className="hover:bg-gray-700/30 transition-colors">
                                                    <td className="p-4 font-bold text-white">{studio.studio_name}</td>
                                                    <td className="p-4 text-gray-300">{studio.owner_name}</td>
                                                    <td className="p-4">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${safeStatus === 'Approved' || safeStatus === 'Active' ? 'bg-green-500/20 text-green-400' :
                                                            safeStatus === 'Suspended' ? 'bg-orange-500/20 text-orange-400' :
                                                                safeStatus === 'Removed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                                            {safeStatus}
                                                        </span>
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex gap-2">
                                                            {studio.role !== 'superadmin' && (safeStatus === 'Approved' || safeStatus === 'Active') && (
                                                                <button onClick={() => updateStudioStatus(studio.id, 'Suspended')} className="bg-orange-600 hover:bg-orange-700 text-xs px-3 py-1.5 rounded">Suspend</button>
                                                            )}
                                                            {studio.role !== 'superadmin' && safeStatus === 'Suspended' && (
                                                                <button onClick={() => updateStudioStatus(studio.id, 'Approved')} className="bg-green-600 hover:bg-green-700 text-xs px-3 py-1.5 rounded">Activate</button>
                                                            )}
                                                            {studio.role !== 'superadmin' && (
                                                                <button onClick={() => updateStudioStatus(studio.id, 'Removed')} className="bg-red-900/50 hover:bg-red-900 text-red-500 border border-red-800 text-xs px-3 py-1.5 rounded">Remove</button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* PENDING APPROVALS TAB */}
                {activeTab === 'pending' && (
                    <PendingStudios />
                )}

                {/* ALL STUDIOS TAB */}
                {activeTab === 'studios' && (
                    <StudioList
                        studios={studios}
                        onUpdateStatus={updateStudioStatus}
                        onEditStudio={updateStudio}
                    />
                )}

                {activeTab === 'affiliate' && (
                    <AffiliateSettings />
                )}

            </div>
        </div>
    );
};

export default SuperAdminDashboard;
